import React, { useState, useEffect } from 'react';
import { Camera } from 'lucide-react';
import { getAbout } from '../services/api';

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const data = await getAbout();
        setAboutData(data);
      } catch (error) {
        console.error('Error loading about info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] pt-20 flex justify-center items-center">
        <div className="text-[#4b63eb] text-lg">Loading...</div>
      </div>
    );
  }

  if (!aboutData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-bold text-[#f5f5f5] mb-4 tracking-wider text-center">
          About <span className="text-[#4b63eb]">Us</span>
        </h1>
        <p className="text-[#a0a0a0] text-lg text-center mb-16">
          Capturing architectural beauty through the lens
        </p>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] opacity-20 blur-2xl"></div>
              <img
                src={aboutData.image}
                alt={aboutData.name}
                className="relative w-full h-auto object-cover shadow-2xl"
              />
            </div>
          </div>

          {/* Text Content */}
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center space-x-2 text-[#4b63eb] mb-4">
              <Camera size={24} />
              <span className="text-sm tracking-widest uppercase font-semibold">Photographer</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#f5f5f5] tracking-wider">
              {aboutData.name}
            </h2>
            <p className="text-xl text-[#4b63eb] italic">{aboutData.company}</p>
            <p className="text-lg text-[#a0a0a0] leading-relaxed">
              {aboutData.bio}
            </p>

            {/* Stats or Highlights */}
            <div className="grid grid-cols-2 gap-6 pt-8">
              <div className="border-l-4 border-[#4b63eb] pl-4">
                <div className="text-3xl font-bold text-[#f5f5f5] mb-1">30+</div>
                <div className="text-sm text-[#a0a0a0] uppercase tracking-wider">Years Experience</div>
              </div>
              <div className="border-l-4 border-[#3b4ec9] pl-4">
                <div className="text-3xl font-bold text-[#f5f5f5] mb-1">200k</div>
                <div className="text-sm text-[#a0a0a0] uppercase tracking-wider">Projects Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#1a1a1a] to-[#0f0f10]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-[#f5f5f5] mb-12 tracking-wider text-center">
            Our <span className="text-[#4b63eb]">Services</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Residential */}
            <div className="bg-[#1a1a1a] p-8 border border-[#2d3748] hover:border-[#4b63eb] transition-all duration-300">
              <h3 className="text-2xl font-bold text-[#f5f5f5] mb-4 tracking-wider">Residential Interiors</h3>
              <p className="text-[#a0a0a0] leading-relaxed mb-4">
                We specialize in capturing the warmth and character of residential spaces. From cozy living rooms to elegant bedrooms, we showcase the unique personality of every home and offer a floorplan with all interior photo shoots.
              </p>
              <ul className="space-y-2 text-[#a0a0a0]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#4b63eb] mt-1">•</span>
                  <span>Living spaces and bedrooms</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#4b63eb] mt-1">•</span>
                  <span>Kitchens and bathrooms</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#4b63eb] mt-1">•</span>
                  <span>Architectural details and features</span>
                </li>
              </ul>
            </div>

            {/* Commercial */}
            <div className="bg-[#1a1a1a] p-8 border border-[#2d3748] hover:border-[#3b4ec9] transition-all duration-300">
              <h3 className="text-2xl font-bold text-[#f5f5f5] mb-4 tracking-wider">Commercial Exteriors</h3>
              <p className="text-[#a0a0a0] leading-relaxed mb-4">
                Our commercial photography highlights the architectural excellence of office buildings, retail spaces, and corporate campuses with dramatic perspectives and professional composition.
              </p>
              <ul className="space-y-2 text-[#a0a0a0]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#3b4ec9] mt-1">•</span>
                  <span>Office buildings and towers</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#3b4ec9] mt-1">•</span>
                  <span>Retail and mixed-use developments</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#3b4ec9] mt-1">•</span>
                  <span>Dusk and twilight photography</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
